import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';

import { DateTime } from 'luxon';

import { Store } from '@ngrx/store';
import { getDetails } from '../../modules/auth/state/auth.state';
import { getLocationList, getSenderLocations, selectLocation } from '../../modules/location-tree/state/location-tree.state';

import { HttpService } from './hezky-fe-services/http.service';
import { RoleAuthorizationService } from './hezky-fe-services/role-authorization.service';

import { combineLatest, distinctUntilChanged, filter, tap, BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzIconService } from 'ng-zorro-antd/icon';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

import { hezky_svg_data } from '../../hezky_svg_data';
import { ENV, TYPE } from '../../hezky-routes';
import { selectAbbreviations, selectApplicationWiseCapabilities } from '../../state/app.state';
import { region } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FeUtilityService implements OnDestroy {
  private destroy$ = new Subject<void>();
  temp_pref: 'D' | 'F' = 'D';
  time_zone!: string;
  assignedLocations: number[] = [];
  commonDateFormat: string = "yyyy-LL-dd'T'HH:mm:ssZZ";
  nLevelPayload: { location_id: number[], timezone: string } = {
    location_id: [],
    timezone: 'Asia/Kolkata',
  }
  user_data!: TYPE.UserData;
  user_name!: string;
  applications: TYPE.Application[] = [];
  displayProducts = new BehaviorSubject<TYPE.Application | null>(null);
  getDisplayProduct = this.displayProducts.asObservable();
  isProduction: boolean = ENV.production;
  private messageShown = false;
  private _selectedApp: string = '';
  private _appId: number = 0;

  get selectedApp(): string {
    if (this._selectedApp) return this._selectedApp;
    try {
      const raw = localStorage.getItem('selectedApplication');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.application_name) {
          this._selectedApp = parsed.application_name.toLowerCase();
          return this._selectedApp;
        }
      }
    } catch { /* ignore parse errors */ }
    return '';
  }

  set selectedApp(value: string) {
    this._selectedApp = value?.toLowerCase() ?? '';
  }

  get appId(): number {
    if (this._appId) return this._appId;
    try {
      const raw = localStorage.getItem('selectedApplication');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.application_id) {
          this._appId = parsed.application_id;
          return this._appId;
        }
      }
    } catch { /* ignore parse errors */ }
    return 0;
  }

  set appId(value: number) {
    this._appId = value;
  }
  isExtLocAllowed: boolean = false;

  get getOriginEnv(): 'dev' | 'stage' | 'prod' | 'pre-prod' {
    return ENV.origin as 'dev' | 'stage' | 'prod' | 'pre-prod';
  }

  get getRegion(): string {
    return region;
  }

  // Is Sender exists flag
  private isSenderExists: boolean = false;
  get senderExists(): boolean {
    return this.isSenderExists;
  }
  set senderExists(value: boolean) {
    this.isSenderExists = value;
  }

  constructor(
    public readonly httpSvc: HttpService,
    public readonly store: Store,
    public readonly router: Router,
    public readonly fb: FormBuilder,

    public readonly roleService: RoleAuthorizationService,
    public readonly drawerSvc: NzDrawerService,
    public readonly message: NzMessageService,
    public readonly notification: NzNotificationService,
    public readonly modalSvc: NzModalService,

    private readonly nzIconSvc: NzIconService,
  ) {
    this.handleUserAndLocationChanges();
    this.initializeCustomIcons();
  }

  /**
   * Handles changes to user and location data.
   * Synchronizes user preferences and location data with the application state.
   */
  private handleUserAndLocationChanges(): void {
    combineLatest([this.store.select(getDetails), this.store.select(selectLocation)])
      .pipe(
        filter(([userDetails]) => !!userDetails),
        distinctUntilChanged(),
        tap(([userDetails, location]) => {
          this.temp_pref = userDetails?.temp_preference;
          this.time_zone = userDetails?.timezone;
          this.user_data = userDetails;
          this.user_name = `${this.user_data?.first_name ?? ''} ${this.user_data?.last_name ?? ''}`.trim();
          this.assignedLocations = userDetails?.accessLocation ?? [];
          this.nLevelPayload['location_id'] = location ?? this.assignedLocations;
          this.nLevelPayload['timezone'] = this.time_zone;
          if(this.applications?.length === 0) this.handleApplications(userDetails?.application_id);
        }),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          this.handleSenderLocations();
        }
      });
  }

  private handleSenderLocations(): void {
    combineLatest([this.store.select(getSenderLocations), this.store.select(getLocationList)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([senderLocations, locationList]: [any[] | null, any[] | null]) => {
        const safeSenderLocations = senderLocations ?? [];
        const safeLocationList = locationList ?? [];
        const locationIds = new Set(
          Array.isArray(safeLocationList)
            ? safeLocationList.map((location: { value: number }) => location.value)
            : []
        );
        this.senderExists = Array.isArray(safeSenderLocations)
          ? safeSenderLocations.some((loc: { value: number }) => locationIds.has(loc.value))
          : false;
      });
  }

  /**
   * Single source of truth for switching the active application.
   * Updates in-memory state, localStorage, and the displayProducts stream.
   * Call this everywhere an app switch occurs (header, notification, permission, etc.).
   */
  public switchApplication(appId: number): void {
    const app = this.applications?.find((a: TYPE.Application) => a.id === appId);
    if (!app) return;
    this.appId = appId;
    this.selectedApp = (app.name || '').toLowerCase();
    localStorage.setItem('selectedApplication', JSON.stringify({
      application_id: appId,
      application_name: app.name
    }));
    this.displayProducts.next(app);
  }

  /**
   * Handles and augments the applications array, and sets selected application state.
   * @param applications Array of application objects
   * @returns Updated array of applications
   */
  public handleApplications(applications: TYPE.Application[]): TYPE.Application[] {
    let savedStateApplications = applications;
    if (savedStateApplications?.length && savedStateApplications?.length > 1) {
      const prependItem: TYPE.Application = {
        id: 0,
        name: 'corporate',
        allowedOrigin: ['dev', 'stage', 'pre-prod', 'prod'],
        custom: true
      };
      const appendItem: TYPE.Application[] = [{
        id: 3,
        name: 'aqi',
        allowedOrigin: ['dev'],
        custom: true
      },
      {
        id: 4,
        name: 'manufacture',
        allowedOrigin: ['dev'],
        custom: true
      }];
      savedStateApplications = [prependItem, ...savedStateApplications, ...appendItem];
    }
    this.applications = savedStateApplications as TYPE.Application[];
    if (this.applications?.length) {
      let localStrSelectedApp = localStorage.getItem('selectedApplication');
      const parsedApp = JSON.parse(localStrSelectedApp || '{}');
      this.selectedApp = parsedApp.application_name?.toLowerCase() ?? '';
      this.appId = parsedApp.application_id;
      this.displayProducts.next(this.applications?.find((app: TYPE.Application) => app.name?.toLowerCase() === this.selectedApp) || null);
    }
    return this.applications
  }

  /**
   * Initializes custom icons by adding them to the NzIconService.
   * Uses a for...of loop for better performance and avoids creating unnecessary function objects.
   */
  private initializeCustomIcons(): void {
    for (let i = 0, len = hezky_svg_data.length; i < len; i++) {
      const item = hezky_svg_data[i];
      this.nzIconSvc.addIconLiteral(`hezky:${item.name}`, item.value);
    }
  }
  
  /**
   * Recursively validates a form group by marking all of its controls as touched
   * and updating their validity.
   *
   * @param form The form group to validate.
   */
  private validateFormGroup(form: FormGroup): void {
    this.getFormErrors(form);
    Object.values(form.controls).forEach((formControl: AbstractControl) => {
      const controlValue = (formControl as any).value;
      if (formControl instanceof FormArray) {
        return;
      }
      if (formControl.invalid) {
        formControl.markAsTouched({ onlySelf: true });
        formControl.updateValueAndValidity({ onlySelf: true });
      }
      if (this.isEmpty(controlValue)) {
        formControl.reset();
        formControl.markAsTouched({ onlySelf: true });
        formControl.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  public isEmpty(value: any): boolean {
    return (typeof value === 'string' && value.trim().length === 0);
  }

  /**
   * Validates a form group and returns true if the form is valid, false otherwise.
   * If the form is invalid, it will mark all controls as touched and update their validity.
   * It will also display a message to the user indicating that all required fields must be filled.
   *
   * @param form The form group to validate.
   * @returns True if the form is valid, false otherwise.
   */
  public validate(form: FormGroup): boolean {
    let isEmpty = Object.values(form.controls).some((control: AbstractControl) => this.isEmpty((control as any).value));
    if (form.invalid || isEmpty) {
      this.validateFormGroup(form);
      if (!this.messageShown) {
        this.message.info('Please fill all required fields!');
        this.messageShown = true;
        setTimeout(() => {
          this.messageShown = false;
        }, 2000);
      }
      return false;
    }
    return true;
  }
  
  private parseDateFlexible(value: string, format: string): DateTime {
    const attempts = [
      DateTime.fromFormat(value, format),
      DateTime.fromISO(value),
      DateTime.fromRFC2822(value),
      DateTime.fromHTTP(value),
      DateTime.fromSQL(value),
    ];
    return attempts.find(dt => dt.isValid) ?? DateTime.invalid('unparseable');
  }

  public getDateDifferenceBetweenTwoDates(startDate: string | null, endDate: string | null, format: string = 'yyyy-MM-dd HH:mm:ss'): number {
    if (!startDate || !endDate || typeof startDate !== 'string' || typeof endDate !== 'string') return 0;
    const start = this.parseDateFlexible(startDate, format);
    const end = this.parseDateFlexible(endDate, format);
    if (!start.isValid || !end.isValid) return 0;
    return end.diff(start).milliseconds;
  }

  public getDaysDiffInNumber(exp: string | number): number {
    const todayDT = DateTime.now().startOf('day');
    let expDT: DateTime | null = null;
    if (typeof exp === 'string') {
      const numeric = Number(exp);
      if (!isNaN(numeric)) {
        if (numeric >= 1e10) {
          expDT = DateTime.fromMillis(numeric).startOf('day');
        } else {
          expDT = todayDT.plus({ days: numeric });
        }
      } else {
        expDT = DateTime.fromISO(exp).isValid ? DateTime.fromISO(exp).startOf('day') : DateTime.invalid('Invalid expiration value');
      }
    }
    if (typeof exp === 'number') {
      expDT = DateTime.fromMillis(exp).startOf('day');
    }
    if (!expDT) return NaN;
    return Math.floor(expDT.diff(todayDT, 'days').days);
  }
  
  /**
   * Gets the formatted value for a given sensor type and matrix key.
   * If no sensor is found with the given type or if the sensor's value is null,
   * an empty string is returned.
   * @param data - The array of sensor data.
   * @param sensor_type - The type of sensor to get the value for.
   * @param matrixKey - The key to get the value from.
   * @returns The formatted value, or an empty string if no value is found.
   */
  public getFormattedValue(data: any[], sensor_type: string, matrixKey: string): string | boolean {
    const sensor = data?.find((s) => s?.sensor_type === sensor_type) ?? null;
    if (!sensor) return false;
    if (!sensor || sensor[matrixKey] === null) return '';
    return this.getFormattedTemp(sensor[matrixKey], matrixKey);
  }

  /**
   * Formats a given temperature value according to the current temperature preference.
   * @param value - The temperature value to format.
   * @param matrixKey - The matrix key to format the value for. Supported keys are 'temperature', 'pressure', and 'humidity'.
   * @returns A formatted string representing the value.
   * @example
   * const value = 25;
   * const matrixKey = 'temperature';
   * const formattedValue = feUtil.getFormattedTemp(value, matrixKey);
   * // formattedValue is '25°C'
   */
  public getFormattedTemp(value: number | null, matrixKey: string): string {
    let matrix = matrixKey?.toLowerCase();
    if (value === null || value === undefined) return '-';
    const num = Number(value);
    if (matrixKey === 'temperature') return `${this.temp_pref === 'D' ? num.toFixed(1) : (num * 1.8 + 32).toFixed(1)}${this.getMatrixSymbol(matrix)}`;
    if (matrixKey === 'pressure') return `${num?.toFixed(1)}${this.getMatrixSymbol(matrix)}`;
    if (matrixKey === 'humidity') return `${num?.toFixed(1)}${this.getMatrixSymbol(matrix)}`;
    if (matrixKey === 'illuminance') return `${num?.toFixed(1)}${this.getMatrixSymbol(matrix)}`;
    return value.toString();
  }

  /**
   * Checks if any sensor in the given data has the given capability.
   * @param data - The data object to check.
   * @param matrix - The capability to check for.
   * @returns True if any sensor has the given capability, false otherwise.
   */
  public checkCapabilities(data: any, matrix: string): boolean {
    return data?.sensors?.some((sensor: any) =>
      sensor?.capabilities.includes(matrix)
    );
  }

  /**
   * Returns the appropriate symbol for a given matrix type.
   * Symbols are based on the current temperature preference for temperature,
   * and standard units for humidity and pressure.
   * 
   * @example
   * const matrix = 'temperature';
   * const symbol = feUtil.getMatrixSymbol(matrix);
   * // symbol is '°C'
   * 
   * @param matrix - The matrix type ('temperature', 'humidity', 'pressure').
   * @returns The corresponding symbol for the matrix type.
   *          Returns an empty string if the matrix type is unrecognized.
   */
  public getMatrixSymbol(matrix: string): string {
    let outputSymbol: string = '';
    this.store.select(selectApplicationWiseCapabilities).pipe(take(1)).subscribe((capabilities) => {
      let cap = capabilities?.[this.appId]?.find((cap: any) => cap.name === matrix);
      if (cap?.name == 'temperature') {
        outputSymbol = this.temp_pref === 'D' ? "°C" : "°F";
        return outputSymbol;
      }
      if (cap) outputSymbol = cap?.unit || '';
      return outputSymbol;
    })
    return outputSymbol;
  }

  /**
   * Returns the value to use for tracking an item in an NgFor loop.
   * If the item has an 'id' property, that value is returned. Otherwise, the loop index is returned.
   * 
   * @param index - The loop index.
   * @param item - The item being looped over.
   * @returns The value to use for tracking the item.
   */
  public trackByFn(index: number, item: any): any {
    return item?.id ?? index;
  }

  /**
   * Groups the given data array by sensor type.
   * @param data - The data array to group.
   * @returns An object with the sensor type as the key and an array of sensors as the value.
   *          The sensor type is trimmed and converted to lowercase before being used as the key.
   * @example
   * const data = [
   *   { sensor_type: 'hot spot', temperature: 25 },
   *   { sensor_type: 'cold spot', temperature: 20 },
   * ];
   * const groupedData = feUtil.groupBySensorType(data);
   * // groupedData is { 'hot spot': [{ sensor_type: 'hot spot', temperature: 25 }],
   * //                    'cold spot': [{ sensor_type: 'cold spot', temperature: 20 }] }
   */
  public groupBySensorType(data: any[]): Record<string, any[]> {
    return data.reduce((acc, sensor) => {
      const type = sensor.sensor_type?.trim().toLowerCase() || 'unknown';
      acc[type] = acc[type] || [];
      acc[type].push(sensor);
      return acc;
    }, {} as Record<string, any[]>);
  }

  /**
   * Groups the given data array by the given key type.
   * @param data - The data array to group.
   * @param key - The key type to group by.
   * @returns An object with the key type as the key and an array of sensors as the value.
   *          The key type is trimmed and converted to lowercase before being used as the key.
   * @example
   * const data = [
   *   { sensor_type: 'hot spot', temperature: 25 },
   *   { sensor_type: 'cold spot', temperature: 20 },
   * ];
   * const groupedData = feUtil.groupByKey(data, 'sensor_type');
   * // e.g. groupedData = { 'hot spot': [{ sensor_type: 'hot spot', temperature: 25 }], 'cold spot': [{ sensor_type: 'cold spot', temperature: 20 }] }
   */
  public groupByKey(data: any[], key: string): Record<string, any[]> {
    if (data) {
      return data?.reduce((acc, data) => {
        const type = typeof data[key] === 'string' ? data[key]?.trim() : data[key]?.toString()?.trim() || 'unknown';
        acc[type] = acc[type] || [];
        acc[type].push(data);
        return acc;
      }, {} as Record<string, any[]>);
    }
    return {};
  }

  /**
   * Returns the color for a given sensor type and matrix type.
   * @param sensorType - The type of sensor.
   * @param matrixType - The type of matrix.
   * @returns A color string in the format #RRGGBB.
   * @example
   * const color = feUtil.getColor('hot spot', 'temperature');
   * // color is #0165FF
   */
  public getColor(sensorType: string = '', matrixType: string): string {
    let outputColor: string = '#FF9F40';
    if(!sensorType || sensorType === '') {
      this.store.select(selectApplicationWiseCapabilities).pipe(take(1)).subscribe((capabilities) => {
        let cap = capabilities?.[this.appId]?.find((cap: any) => cap.name === matrixType);
        if(cap) outputColor = cap?.color || '#FF9F40';
      })
    }
    else {
      this.store.select(selectAbbreviations).pipe(take(1)).subscribe((abb) => {
        if(abb) {
          const abbreviation = abb.find((abb: any) => abb.description === sensorType);
          outputColor = abbreviation?.['color_code']?.[matrixType] || '#FF9F40';
        }
      })
    }
    return outputColor;
  }

  /**
   * Converts the given data to a format that can be used by the NzTreeSelectComponent.
   * @param data - The data to convert.
   * @returns The converted data.
   */
  public convertToNzTree(data: any): TYPE.NzTreeNode[] {
    return this.mapDataToNzTree(data);
  }

  private mapDataToNzTree(groups: any[]): TYPE.NzTreeNode[] {
    if (!Array.isArray(groups) || groups.length === 0) return [];
    const buildLocationNodes = (locations: any[], parentLevel: number): TYPE.NzTreeNode[] => {
      if (!Array.isArray(locations) || locations.length === 0) return [];
      return locations
        .filter((location: any) => location && location.location_id)
        .map((location: any) => ({
          title: location.display_name || location.registered_name || `Location ${location.location_id}`,
          key: `${location.location_id}`,
          isLeaf: true,
          locData: location,
          level: parentLevel + 1,
          type: 'location',
          expandable: false
        }));
    };
    const processGroup = (group: any, parentKey: string = '', parentNestedGroupName: string = ''): TYPE.NzTreeNode => {
      const groupKey = `g-${group.group_id}`;
      const nestedGroupNameStr = `g-${group.group_name}`;
      const nodeLevel = group.level !== undefined ? group.level : (parentKey ? parentKey.split('-').length - 1 : 0);

      const node: TYPE.NzTreeNode = {
        title: group.group_name || 'Unnamed Group',
        key: parentKey ? `${parentKey}-${groupKey}` : groupKey,
        nestedGroupName: parentNestedGroupName ? `${parentNestedGroupName}-${nestedGroupNameStr}` : nestedGroupNameStr,
        children: [],
        isLeaf: false,
        level: nodeLevel,
        type: 'group',
        expandable: true,
      };

      // Locations (as leaf children)
      if (Array.isArray(group.locations) && group.locations.length > 0) {
        node.children = node.children!.concat(buildLocationNodes(group.locations, nodeLevel));
      }

      // Child groups (as branch children)
      if (Array.isArray(group.children) && group.children.length > 0) {
        for (const childGroup of group.children) {
          node.children!.push(processGroup(childGroup, node.key, node.nestedGroupName));
        }
      }

      if (!node.children || node.children.length === 0) {
        node.isLeaf = true;
        node.expandable = false;
      }
      return node;
    };
    return groups
      .filter(group => group.parent_group_id === null || group.parent_group_id === undefined)
      .map(group => processGroup(group));
  }

  public convertNzTreeToList(tree: TYPE.NzTreeNode[]): { label: string; value: number }[] {
    const result: { label: string; value: number }[] = [];
    const traverse = (nodes: TYPE.NzTreeNode[]) => {
      for (const node of nodes) {
        if (node.type === 'location') {
          result.push({
            label: node.title,
            value: Number(node.key),
          });
        }
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
    traverse(tree);
    return result;
  }

  /**
   * Returns an array of invalid form controls and their errors.
   * @param form - The form to check for errors.
   * @returns An array of objects containing the control name and its errors.
   */
  public getFormErrors(form: FormGroup): void {
    const invalidControls: { name: string; errors: any }[] = [];
    const checkFormGroupRecursively = (formGroup: FormGroup, parentPath = ''): void => {
      Object.keys(formGroup.controls).forEach(controlName => {
        const control = formGroup.controls[controlName];
        const currentPath = parentPath ? `${parentPath}.${controlName}` : controlName;
        if (control instanceof FormGroup) {
          checkFormGroupRecursively(control, currentPath);
        } else if (control.invalid) {
          invalidControls.push({
            name: currentPath,
            errors: control.errors
          });
        }
      });
    };
    checkFormGroupRecursively(form);
    if (invalidControls.length > 0) {
      console.log(invalidControls);
    }
  }

  /**
   * Sorts sensors array to prioritize hot spot sensors
   * @param sensors - Array of sensors to sort
   * @returns Sorted array with hot spot sensors first
   */
  public sortHotSpotFirst(sensors: any[]): any[] {
    return sensors?.sort((a, b) => (a?.sensor_type === 'hot spot' ? -1 : b?.sensor_type === 'hot spot' ? 1 : 0)) || [];
  }

  public getSeverityColor(severity: string): string {
    severity = severity?.toLowerCase();
    if (severity?.includes('high')) return 'text-danger';
    if (severity?.includes('medium')) return 'text-medium';
    if (severity?.includes('low')) return 'text-warning';
    return '';
  }

  public checkPasswordExpiration(passwordDate: string | null | undefined): { status: 'warning' | 'valid', daysRemaining?: number } {
    if (!passwordDate) {
      return { status: 'valid' };
    }
    const passwordChangeDate = DateTime.fromISO(passwordDate);
    const currentDate = DateTime.now();
    const daysSinceChange = currentDate.diff(passwordChangeDate, 'days').days;
    const daysUntilExpiration = 45 - daysSinceChange;
    const warningThreshold = 7;
    if (daysUntilExpiration > 0 && daysUntilExpiration <= warningThreshold) {
      return { status: 'warning', daysRemaining: Math.ceil(daysUntilExpiration) };
    }
    return { status: 'valid' };
  }

  public showPasswordExpirationWarning(daysRemaining: number): void {
    const title = 'Password Expiring Soon';
    const content = `Your password will expire in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Please update your password to avoid account access issues.`;
    this.modalSvc.warning({
      nzTitle: title,
      nzContent: content,
      nzOkText: 'Change Password',
      nzCancelText: 'Later',
      nzOnOk: () => {
        this.router.navigate(['/account-settings']);
      },
      nzWidth: 500
    });
  }

  /**
   * Recursively finds the diff between the original and current form values.
   * Returns an object containing only changed fields (deep diff for objects and arrays),
   * but always retains all items in arrays (with unchanged items left as-is).
   */
  public checkDiff(original: any, newDataRaw?: any): any {
    const newData = newDataRaw ?? {};
    if (!original) {
      return { ...newData };
    }
    if (typeof newData !== 'object' || newData === null) {
      return newData !== original ? newData : undefined;
    }

    if (Array.isArray(newData) && Array.isArray(original)) {
      if (newData.length !== original.length) {
        return newData;
      }
      const arrDiff = newData.map((item: any, idx: number) => {
        const childDiff = this.checkDiff(original[idx], item);
        return typeof childDiff === 'undefined' ? original[idx] : (typeof childDiff === 'object' && Object.keys(childDiff).length === 0 ? original[idx] : item);
      });
      const changed = arrDiff.some((item, idx) => item !== original[idx]);
      return changed ? arrDiff : undefined;
    }
    const diff: any = {};
    Object.keys(newData).forEach(key => {
      const origVal = original[key];
      const newVal = newData[key];
      if (typeof newVal === 'object' && newVal !== null && origVal !== undefined) {
        const childDiff = this.checkDiff(origVal, newVal);
        if (
          childDiff !== undefined &&
          ((typeof childDiff === 'object' && Object.keys(childDiff).length > 0) || (typeof childDiff !== 'object'))
        ) {
          diff[key] = childDiff;
        }
      } else {
        if (newVal !== origVal) {
          diff[key] = newVal;
        }
      }
    });
    return diff;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public downloadS3File(
    fileOrFiles: { doc_id: number, doc_name: string, doc_link: string } | { doc_id: number, doc_name: string, doc_link: string }[]
  ): void {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    files.forEach(file => {
      if (file?.doc_link) {
        fetch(file.doc_link)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.doc_name;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }, 0);
          })
          .catch(() => {
            this.message.error('Failed to download file', { nzDuration: 2000 });
          });
      }
    });
  }
}